import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { AUTH_EXPIRED, expireSession } from '../utils/authSession';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const generation = useRef(0);

  const checkAuth = useCallback(async () => {
    const attempt = ++generation.current;
    const token = localStorage.getItem('auth_token');
    setAuthError(null);
    setUser(null);
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const response = await axios.get(`${getApiUrl()}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
      });
      if (attempt !== generation.current || localStorage.getItem('auth_token') !== token) return;
      if (response.data.valid && response.data.user) {
        setUser(response.data.user);
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
      } else {
        expireSession(token);
      }
    } catch (error) {
      if (attempt !== generation.current || localStorage.getItem('auth_token') !== token) return;
      if (error.response?.status === 401) expireSession(token);
      else setAuthError('Не удалось проверить сессию: сервер недоступен. Повторите попытку.');
    } finally {
      if (attempt === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const expired = () => {
      generation.current += 1;
      setUser(null); setAuthError(null); setLoading(false);
    };
    const changed = (e) => {
      if (e.key === null || e.key === 'auth_token' || e.key === 'user_data') checkAuth();
    };
    window.addEventListener(AUTH_EXPIRED, expired);
    window.addEventListener('storage', changed);
    checkAuth();
    return () => {
      generation.current += 1;
      window.removeEventListener(AUTH_EXPIRED, expired);
      window.removeEventListener('storage', changed);
    };
  }, [checkAuth]);

  const login = (userData) => {
    generation.current += 1;
    sessionStorage.removeItem('auth_message');
    setUser(userData); setAuthError(null); setLoading(false);
  };
  const logout = () => {
    generation.current += 1;
    localStorage.removeItem('auth_token'); localStorage.removeItem('user_data');
    setUser(null); setAuthError(null); setLoading(false);
  };
  return <AuthContext.Provider value={{ user, loading, authError, checkAuth, login, logout,
    isAdmin: () => user?.role === 'admin', isViewer: () => user?.role === 'viewer',
    hasFullAccess: () => user?.full_access === true }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
